import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, MapPin, Sparkles, Briefcase, ImagePlus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import InitialAvatar from "@/components/InitialAvatar";
import { fileToImageDataUrl } from "@/lib/hooks";
import { getInterestsForIntents } from "@/lib/onboardingData";
import BusinessEditProfile from "@/screens/profile/BusinessEditProfile";

const DAYS = 30 * 24 * 60 * 60 * 1000;

const BUSINESS_CATEGORIES = [
    "Makeup", "Photography", "Videography", "DJ", "Events", "Catering",
    "Fitness", "Beauty", "Hair", "Nails", "Tech", "Tutoring", "Fashion",
    "Cleaning", "Home Services", "Wellness", "Other",
];

// Default interest pool (covers all intents) — used so existing users can edit
// even when their onboarding intents aren't loaded.
const ALL_INTENT_IDS = [
    "discover-events",
    "join-activities",
    "find-services",
    "provide-services",
    "host-events",
    "meet-people",
    "promote-business",
];

const EditProfile = () => {
    const navigate = useNavigate();
    const { user, updateUser, usernameIsTaken } = useAuth();

    // Phase 3D rule — profile type is locked. Business accounts get the
    // business-only edit form; personal accounts only see personal fields.
    // Effects must run unconditionally — handle the redirect with one.
    useEffect(() => {
        // No redirects from this hook — we render the business form below
        // when accountType is business. Hook kept so the file always has
        // identical hook ordering between renders.
    }, []);
    if (user?.accountType === "business") {
        return <BusinessEditProfile />;
    }

    return <PersonalEditProfileInner navigate={navigate} user={user} updateUser={updateUser} usernameIsTaken={usernameIsTaken} />;
};

const PersonalEditProfileInner = ({ navigate, user, updateUser, usernameIsTaken }) => {
    const [fullName, setFullName] = useState(user?.fullName || "");
    const [username, setUsername] = useState(user?.username || "");
    const [bio, setBio] = useState(user?.bio || "");
    const [location, setLocation] = useState(user?.location || "");
    const [interests, setInterests] = useState(user?.interests || []);
    const [avatar, setAvatar] = useState(user?.avatar || null);
    const [banner, setBanner] = useState(user?.banner || null);
    const [businessCategory, setBusinessCategory] = useState(user?.businessCategory || "");
    const [startingPrice, setStartingPrice] = useState(
        user?.startingPrice != null ? String(user.startingPrice) : ""
    );
    const [portfolio, setPortfolio] = useState(user?.portfolio || []);
    const [errors, setErrors] = useState({});

    // Personal accounts NEVER see business fields here — locked false.
    const isBusiness = false;
    void isBusiness; void businessCategory; void setBusinessCategory;
    void startingPrice; void setStartingPrice; void portfolio; void setPortfolio;

    // Build the available interest pool. If the user has saved intents, use those;
    // otherwise show the union for all intents so they can broaden their picks.
    const interestPool = (() => {
        const intents =
            (user?.intents && user.intents.length ? user.intents : null) ||
            ALL_INTENT_IDS;
        return getInterestsForIntents(intents);
    })();

    const toggleInterest = (id) => {
        setInterests((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const nameLocked =
        user?.nameChangedAt && Date.now() - new Date(user.nameChangedAt).getTime() < DAYS;
    const usernameLocked =
        user?.usernameChangedAt && Date.now() - new Date(user.usernameChangedAt).getTime() < DAYS;

    const daysUntil = (ts) => {
        const left = DAYS - (Date.now() - new Date(ts).getTime());
        return Math.ceil(left / (24 * 60 * 60 * 1000));
    };

    const pickImage = async (file) => {
        if (!file) return;
        try {
            const url = await fileToImageDataUrl(file, 480, 0.82);
            setAvatar(url);
        } catch {
            setErrors({ ...errors, avatar: "Couldn't load that image" });
        }
    };

    const pickBanner = async (file) => {
        if (!file) return;
        try {
            const url = await fileToImageDataUrl(file, 1200, 0.85);
            setBanner(url);
        } catch {
            setErrors({ ...errors, banner: "Couldn't load that image" });
        }
    };

    const addPortfolioImage = async (file) => {
        if (!file) return;
        try {
            const url = await fileToImageDataUrl(file, 900, 0.82);
            setPortfolio((prev) => [...prev, { id: "pf_" + Date.now(), url }].slice(0, 12));
        } catch {
            setErrors({ ...errors, portfolio: "Couldn't load that image" });
        }
    };

    const removePortfolioItem = (id) => {
        setPortfolio((prev) => prev.filter((p) => p.id !== id));
    };

    const handleSave = () => {
        const e = {};
        const changingName = fullName.trim() !== user.fullName;
        const changingUsername = username.trim() !== user.username;

        if (!fullName.trim() || fullName.trim().length < 2) e.fullName = "Enter your full name";
        if (changingName && nameLocked)
            e.fullName = `You can change your name in ${daysUntil(user.nameChangedAt)} days`;

        if (!/^[a-zA-Z0-9_.]{3,20}$/.test(username)) e.username = "3–20 chars, letters, numbers, _ or .";
        else if (changingUsername && usernameLocked)
            e.username = `You can change your username in ${daysUntil(user.usernameChangedAt)} days`;
        else if (changingUsername && usernameIsTaken(username)) e.username = "Username already taken";

        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }

        const patch = {
            fullName: fullName.trim(),
            username: username.trim(),
            bio: bio.trim() || null,
            location: location.trim() || null,
            interests,
            avatar,
        };
        if (isBusiness) {
            patch.banner = banner || null;
            patch.businessCategory = businessCategory.trim() || null;
            patch.startingPrice = startingPrice ? Number(startingPrice) : null;
            patch.portfolio = portfolio;
        }
        if (changingName) patch.nameChangedAt = new Date().toISOString();
        if (changingUsername) patch.usernameChangedAt = new Date().toISOString();

        updateUser(patch);
        navigate(-1);
    };

    return (
        <div className="screen" data-testid="edit-profile-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60">
                <div className="flex items-center justify-between">
                    <button
                        data-testid="edit-profile-back"
                        onClick={() => navigate(-1)}
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <h1 className="font-display text-[16px] font-semibold">Edit profile</h1>
                    <button
                        data-testid="edit-profile-save"
                        onClick={handleSave}
                        className="h-10 px-4 rounded-full bg-primary text-primary-foreground text-[13px] font-medium active:scale-[0.98]"
                    >
                        Save
                    </button>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-5">
                <div className="flex flex-col items-center gap-3">
                    <div className="relative">
                        {avatar ? (
                            <img src={avatar} alt="" className="h-28 w-28 rounded-full object-cover ring-2 ring-border" />
                        ) : (
                            <InitialAvatar name={fullName || user?.fullName || "?"} size={112} />
                        )}
                        <label
                            className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full flex items-center justify-center bg-primary text-primary-foreground shadow-card cursor-pointer active:scale-95"
                            data-testid="edit-profile-avatar-label"
                        >
                            <Camera size={16} />
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                data-testid="edit-profile-avatar-input"
                                onChange={(e) => pickImage(e.target.files?.[0])}
                            />
                        </label>
                    </div>
                </div>

                <Field
                    label="Full name"
                    value={fullName}
                    onChange={setFullName}
                    error={errors.fullName}
                    testId="edit-profile-fullname"
                    hint={nameLocked ? `Locked for ${daysUntil(user.nameChangedAt)} more days` : null}
                />
                <Field
                    label="Username"
                    value={username}
                    onChange={(v) => setUsername(v.replace(/\s+/g, ""))}
                    error={errors.username}
                    testId="edit-profile-username"
                    hint={usernameLocked ? `Locked for ${daysUntil(user.usernameChangedAt)} more days` : null}
                    prefix="@"
                />
                <div>
                    <label className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                        Bio
                    </label>
                    <textarea
                        data-testid="edit-profile-bio"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        maxLength={140}
                        rows={3}
                        placeholder="Tell people about yourself"
                        className="w-full p-3.5 rounded-2xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[14px] resize-none"
                    />
                    <div className="text-right text-[11px] text-muted-foreground mt-1">
                        {bio.length}/140
                    </div>
                </div>

                {/* Location */}
                <Field
                    label="Location"
                    value={location}
                    onChange={setLocation}
                    error={errors.location}
                    testId="edit-profile-location"
                    prefix={<MapPin size={14} className="text-muted-foreground mr-2" />}
                    placeholder="e.g. Lekki, Lagos"
                />

                {/* Interests */}
                <div className="rounded-2xl border border-border bg-card p-4 shadow-soft" data-testid="edit-profile-interests">
                    <div className="flex items-center justify-between mb-3">
                        <span className="block text-[12px] font-medium text-muted-foreground inline-flex items-center gap-1.5 ml-1">
                            <Sparkles size={12} className="text-primary" />
                            Interests
                        </span>
                        <span className="text-[11px] font-medium text-primary">
                            {interests.length} selected
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {interestPool.map((i) => {
                            const Icon = i.icon;
                            const selected = interests.includes(i.id);
                            return (
                                <button
                                    type="button"
                                    key={i.id}
                                    data-testid={`edit-interest-${i.id}`}
                                    onClick={() => toggleInterest(i.id)}
                                    className={[
                                        "inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-medium border transition-colors active:scale-[0.97]",
                                        selected
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-secondary text-foreground border-border hover:bg-muted",
                                    ].join(" ")}
                                >
                                    {Icon && <Icon size={12} />}
                                    {i.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Business profile extras — only for business accounts */}
                {isBusiness && (
                    <div className="space-y-4" data-testid="edit-profile-business-section">
                        <div className="rounded-2xl border border-border bg-card p-4 shadow-soft space-y-4">
                            <span className="block text-[12px] font-medium text-muted-foreground inline-flex items-center gap-1.5 ml-1">
                                <Briefcase size={12} className="text-primary" />
                                Business profile
                            </span>

                            {/* Banner */}
                            <div>
                                <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                    Banner
                                </span>
                                <label
                                    className="relative block h-28 w-full rounded-2xl overflow-hidden border border-dashed border-border bg-secondary cursor-pointer"
                                    data-testid="edit-business-banner-label"
                                >
                                    {banner ? (
                                        <img src={banner} alt="" className="absolute inset-0 h-full w-full object-cover" />
                                    ) : (
                                        <span className="absolute inset-0 flex items-center justify-center gap-2 text-[12.5px] text-muted-foreground">
                                            <ImagePlus size={15} />
                                            Upload banner
                                        </span>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        data-testid="edit-business-banner-input"
                                        onChange={(e) => pickBanner(e.target.files?.[0])}
                                    />
                                </label>
                            </div>

                            {/* Category */}
                            <div>
                                <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                    Category
                                </span>
                                <select
                                    data-testid="edit-business-category"
                                    value={businessCategory}
                                    onChange={(e) => setBusinessCategory(e.target.value)}
                                    className="w-full h-12 px-3 rounded-xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[14px]"
                                >
                                    <option value="">Choose a category</option>
                                    {BUSINESS_CATEGORIES.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Starting price */}
                            <div>
                                <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                    Starting price (₦)
                                </span>
                                <input
                                    data-testid="edit-business-starting-price"
                                    type="number"
                                    min="0"
                                    value={startingPrice}
                                    onChange={(e) => setStartingPrice(e.target.value)}
                                    placeholder="e.g. 15000"
                                    className="w-full h-12 px-3.5 rounded-xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[14px]"
                                />
                            </div>

                            {/* Portfolio */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5 ml-1">
                                    <span className="text-[12px] font-medium text-muted-foreground">
                                        Portfolio
                                    </span>
                                    <span className="text-[11px] text-muted-foreground">
                                        {portfolio.length}/12
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2" data-testid="edit-business-portfolio">
                                    {portfolio.map((p) => (
                                        <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden border border-border">
                                            <img src={p.url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                                            <button
                                                type="button"
                                                data-testid={`edit-business-portfolio-remove-${p.id}`}
                                                onClick={() => removePortfolioItem(p.id)}
                                                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-background/85 border border-border flex items-center justify-center text-destructive"
                                                aria-label="Remove"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    ))}
                                    {portfolio.length < 12 && (
                                        <label
                                            className="aspect-square rounded-xl border border-dashed border-border bg-secondary flex items-center justify-center gap-1 text-[11.5px] text-muted-foreground cursor-pointer hover:bg-muted"
                                            data-testid="edit-business-portfolio-add-label"
                                        >
                                            <ImagePlus size={14} />
                                            Add
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                data-testid="edit-business-portfolio-input"
                                                onChange={(e) => addPortfolioImage(e.target.files?.[0])}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Field = ({ label, value, onChange, error, testId, hint, prefix, placeholder }) => (
    <label className="block" data-testid={`${testId}-field`}>
        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
            {label}
        </span>
        <span
            className={[
                "flex items-center h-14 px-4 rounded-2xl border transition-colors",
                error
                    ? "border-destructive bg-destructive/5"
                    : "border-border bg-secondary focus-within:border-primary focus-within:bg-background",
            ].join(" ")}
        >
            {typeof prefix === "string" ? (
                <span className="text-muted-foreground mr-1">{prefix}</span>
            ) : prefix || null}
            <input
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/70"
            />
        </span>
        {error ? (
            <span className="block text-[12px] text-destructive mt-1.5 ml-1" data-testid={`${testId}-error`}>
                {error}
            </span>
        ) : hint ? (
            <span className="block text-[12px] text-muted-foreground mt-1.5 ml-1">{hint}</span>
        ) : null}
    </label>
);

export default EditProfile;
